import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { Row, Col } from "reactstrap";

import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";
import { PLACEHOLDERS, LABELS, NAMES } from "../Config";
import { FormikErrorText } from "../../../components/FormikErrorText";
import { AutoComplete } from "../../../components/AutoComplete";
import { customStyles } from "../../UserManagement/CustomStyles";
import GoalStatus, { GOAL_STATUS } from "../../../constants/GoalStatus";
import GoalTypes, { GOAL_TYPES } from "../../../constants/GoalTypes";
import { GOAL_VALIDATION_SCHEMA } from "../FormVaildation";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { useRequestProcessor } from "../../../hooks/useRequestProcessors";
import { getKey, MODULE_KEYS } from "../../../api/api.keys";
import { CreateGoal, GetAudiosSubCategories, UpdateGoal } from "../../../api/api.service";
import RadioGroupV2 from "../../../components/Input/RadioGroupV2";
import { mapToOptions } from "../../../utils/commonUtils";
import { showToast, SUCCESS } from "../../../utils/toastUtils";

let { GOAL_NAME, GOAL_TYPE, GOAL_ORDER, STATUS, SUB_CATEGORY } = LABELS;

const GOAL_STATUS_OPTIONS = Object.keys(GOAL_STATUS).map((key) => ({
    label: GoalStatus.getDisplayTextKey(GOAL_STATUS[key]),
    value: key
}));
const GOAL_TYPE_OPTIONS = Object.keys(GOAL_TYPES).map((key) => ({
    label: GoalTypes.getDisplayTextKey(GOAL_TYPES[key]),
    value: key
}));
const GoalForm = ({ mode, data }) => {
    const history = useHistory();
    const location = useLocation();

    const [subCategoryOptions, setSubCategoryOptions] = useState([]);
    const [hasMoreSubCategories, setHasMoreSubCategories] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const id = location?.pathname?.split("/").pop() || 0;

    let { useFetch, useMutate } = useRequestProcessor();

    let { mutate: addGoal, isLoading } = useMutate("", getKey(), (data) => CreateGoal(data));
    let { mutate: updateGoal, isLoading: isUpdateGoalLoading } = useMutate(
        "",
        getKey(),
        ({ id, bodyData }) => UpdateGoal(id, bodyData)
    );

    const { data: subCategories, isFetching: isFetchingSubCategories } = useFetch(
        getKey([MODULE_KEYS.GOALS_SUB_CATEGORY, currentPage], {}),
        async ({ signal }) =>
            await GetAudiosSubCategories({
                params: { page: currentPage, limit: 10 },
                signal
            }),
        { keepPreviousData: true, enabled: hasMoreSubCategories, staleTime: 0, cacheTime: 0 }
    );

    useEffect(() => {
        if (subCategories?.data) {
            const newOptions = mapToOptions(subCategories.data, "title", "id");
            setSubCategoryOptions((prev) => [...prev, ...newOptions]);
            if (subCategoryOptions.length + newOptions.length >= subCategories.total) {
                setHasMoreSubCategories(false);
            }
        }
    }, [subCategories]);

    const handleScroll = (e, type) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 20) {
            if (type === "subCategory" && hasMoreSubCategories && !isFetchingSubCategories) {
                setCurrentPage((prev) => prev + 1);
            }
        }
    };

    const initialValues = {
        [NAMES[GOAL_NAME]]: data?.name || "",
        [NAMES[GOAL_ORDER]]: data?.priorityNumber || "",
        [NAMES[GOAL_TYPE]]: data?.type
            ? { label: GoalTypes.getDisplayTextKey(GOAL_TYPES[data?.type]), value: data?.type }
            : null,
        [NAMES[STATUS]]: data?.status ? data?.status : null,
        [NAMES[SUB_CATEGORY]]: data?.goalLabels?.length
            ? data?.goalLabels?.map((goal) => ({
                  value: goal?.label?.id,
                  label: goal?.label?.title
              }))
            : null
    };
    let formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: GOAL_VALIDATION_SCHEMA,

        onSubmit: async (values) => {
            try {
                const bodyData = {
                    name: values?.goalName,
                    type: values?.goalType?.value,
                    status:
                        values?.Status === GOAL_STATUS.ACTIVE
                            ? GOAL_STATUS.ACTIVE
                            : GOAL_STATUS.INACTIVE,
                    priorityNumber: values?.goalOrder,
                    labelIds: values?.subCategory?.map((label) => label?.value)
                };

                if (mode === "add") {
                    addGoal(bodyData, {
                        onSuccess: (res) => {
                            showToast(SUCCESS, "Goal Added Successfully!");
                            values?.goalType?.value === GOAL_TYPES.PRIMARY
                                ? history.push(APP_ROUTES.GOAL_MANAGEMENT_PRIMARY)
                                : history.push(APP_ROUTES.GOAL_MANAGEMENT_SECONDARY);
                        }
                    });
                } else {
                    updateGoal(
                        { id, bodyData },
                        {
                            onSuccess: (res) => {
                                showToast(SUCCESS, "Goal Updated Successfully!");
                                values?.goalType?.value === GOAL_TYPES.PRIMARY
                                    ? history.push(APP_ROUTES.GOAL_MANAGEMENT_PRIMARY)
                                    : history.push(APP_ROUTES.GOAL_MANAGEMENT_SECONDARY);
                            }
                        }
                    );
                }
            } catch (error) {
                console.error("Error during form submission:", error);
            }
        }
    });

    const handleCancel = () => {
        formik.resetForm();
        history.replace(APP_ROUTES.GOAL_MANAGEMENT_PRIMARY);
    };

    return (
        <form onSubmit={formik.handleSubmit} className="p-4">
            <Row>
                <Col md={6} className="my-2">
                    <AutoComplete
                        isDisabled={isLoading || isUpdateGoalLoading}
                        classNamePrefix="status-header-search-admin"
                        label={GOAL_TYPE}
                        value={
                            GOAL_TYPE_OPTIONS.find(
                                (option) => option.value === formik.values[NAMES[GOAL_TYPE]]?.value
                            ) || formik.values[NAMES[GOAL_TYPE]]
                        }
                        onChange={(option) => {
                            formik.setFieldValue(NAMES[GOAL_TYPE], option || null);
                        }}
                        onBlur={() => formik.setFieldTouched(NAMES[GOAL_TYPE])}
                        placeholder={PLACEHOLDERS[GOAL_TYPE]}
                        options={GOAL_TYPE_OPTIONS}
                        maxMenuHeight={220}
                        customStyles={customStyles.filter}
                        className=""
                        isClearable
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[GOAL_TYPE]} />
                </Col>
                <Col md={6} className="my-2">
                    <TextInput
                        disabled={isLoading || isUpdateGoalLoading}
                        placeholder={PLACEHOLDERS[LABELS.GOAL_NAME]}
                        label={GOAL_NAME}
                        name={NAMES[GOAL_NAME]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[GOAL_NAME]]}
                        className="hide-default-icon"
                        autoFocus
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[GOAL_NAME]} />
                </Col>

                <Col md={6} className="my-2">
                    <AutoComplete
                        isDisabled={isLoading || isUpdateGoalLoading}
                        classNamePrefix="status-header-search-admin"
                        isLoading={isFetchingSubCategories}
                        handleScroll={(e) => handleScroll(e, "subCategory")}
                        label={SUB_CATEGORY}
                        value={formik.values[NAMES[SUB_CATEGORY]]}
                        onChange={(option) =>
                            formik.setFieldValue(NAMES[SUB_CATEGORY], option || null)
                        }
                        onBlur={() => formik.setFieldTouched(NAMES[SUB_CATEGORY])}
                        placeholder={PLACEHOLDERS[SUB_CATEGORY]}
                        options={subCategoryOptions}
                        maxMenuHeight={220}
                        customStyles={customStyles.filter}
                        isMulti={true}
                        isClearable
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[SUB_CATEGORY]} />
                </Col>
                <Col md={6} className="my-2">
                    <TextInput
                        disabled={isLoading || isUpdateGoalLoading}
                        placeholder={PLACEHOLDERS[LABELS.GOAL_ORDER]}
                        label={GOAL_ORDER}
                        name={NAMES[GOAL_ORDER]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[GOAL_ORDER]]}
                        className="hide-default-icon"
                        type="number"
                        autoFocus
                        wholeNumbers={true}
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[GOAL_ORDER]} />
                </Col>
            </Row>

            <Row className="my-4">
                <label className="input-label">{STATUS}</label>

                <RadioGroupV2
                    field={NAMES[STATUS]}
                    name={NAMES[STATUS]}
                    value={formik.values[NAMES[STATUS]]} // Controlled value from Formik
                    setFieldValue={formik.setFieldValue}
                    // onChange={(value) => formik.setFieldValue(NAMES[STATUS], value)}
                    onBlur={formik.handleBlur}
                    options={GOAL_STATUS_OPTIONS}
                />

                <FormikErrorText formikInstance={formik} fieldName={NAMES[STATUS]} />
            </Row>

            <div className="d-flex justify-content-end w-100 my-2 mt-4">
                <CustomButton
                    type="button"
                    title="Cancel"
                    className="me-3 custom-btn-sm"
                    color="primary"
                    outline
                    onClick={handleCancel}
                />
                <CustomButton
                    loading={isLoading || isUpdateGoalLoading}
                    type="submit"
                    title={mode === "add" ? "Add Goal" : "Update Goal"}
                    color="primary"
                    className="shadow-theme grad-primary"
                />
            </div>
        </form>
    );
};

export default GoalForm;
